import { UserNotification, UserNotificationType } from '../api/notificationsApi';

/**
 * Para qual TELA aponta cada tipo de notificação (at-108).
 *
 * O sino deixou de ser um botão próprio na Sidebar (decisão do Victor, 18/09/2026): o menu mostra
 * só o NÚMERO, no item da tela a que o aviso se refere — o mesmo padrão dos badges que já
 * existiam (aprovações pendentes, SLA, formulários, avisos do Portal). O sino em si passou a
 * viver DENTRO das telas, como no Portal de Cotações.
 *
 * ⚠️ A lista de cada tipo é ORDENADA e vale como "candidatas": o badge vai para a primeira tela
 * que o usuário alcança. Comentário é o caso que obriga a isso — o mesmo aviso chega ao setor de
 * Eventos, ao Chefe e ao colaborador vinculado, e cada um o lê numa tela diferente (ADR 0042).
 */
const TELAS_POR_TIPO: Record<UserNotificationType, string[]> = {
  // Prazos e liberação são leitura do Chefe de Oficina.
  APPROACHING_SECTOR_DEADLINE: ['/chefe-de-oficina'],
  APPROACHING_SHOP_DEADLINE: ['/chefe-de-oficina'],
  CAR_RELEASED: ['/chefe-de-oficina'],
  SERVICO_DEIXADO_INACABADO: ['/chefe-de-oficina'],

  // Tudo que interrompe o trabalho de quem está com a mão no carro.
  SESSION_INTERRUPTED_STATUS_CHANGE: ['/controle-producao'],
  SESSION_INTERRUPTED_COLLABORATOR_CHANGE: ['/controle-producao'],
  SESSION_INTERRUPTED_RELEASE_REMOVED: ['/controle-producao'],
  SESSION_INTERRUPTED_ESPECIALIDADE: ['/controle-producao'],
  DESVINCULADO_POR_ESPECIALIDADE: ['/controle-producao'],

  // at-90: o comentário avisa vinculados, Chefes e o setor de Eventos. A ordem põe o setor de
  // Eventos primeiro porque para ele esta é a ÚNICA tela (ADR 0039) — quem tem as outras
  // também tem a lista do sino lá dentro.
  COMENTARIO_NOVO: ['/andamento-veiculos', '/chefe-de-oficina', '/controle-producao'],
  RESPOSTA_DE_COMENTARIO: ['/andamento-veiculos', '/chefe-de-oficina', '/controle-producao'],

  // at-129: pedido e requisição só se leem no Dashboard de Compras.
  COMENTARIO_COMPRA_NOVO: ['/dashboard-compras'],
  RESPOSTA_DE_COMENTARIO_COMPRA: ['/dashboard-compras'],
  // A menção pode ser de veículo ou de compras; o badge vai à primeira tela alcançada, e o clique
  // no sino é que decide o destino (placa ou referência).
  MENCAO_EM_COMENTARIO: ['/dashboard-compras', '/andamento-veiculos', '/chefe-de-oficina', '/controle-producao'],

  // previsao-pecas/at-02: o aviso de peça que chegou se lê na Previsão de Peças.
  PECA_CHEGOU: ['/previsao-pecas'],

  // at-132: o veículo em oficina externa sem visita se lê na tela de leitura do módulo, que abre
  // já buscando a placa (`?placa=`).
  OFICINA_EXTERNA_SEM_VISITA: ['/oficinas-externas'],
};

/**
 * Ordem de fallback para tipo que o frontend ainda não conhece.
 *
 * ⚠️ Tipo novo no enum do backend sem entrada no mapa acima NÃO pode sumir do menu: o aviso
 * existiria no banco e ninguém veria o número. Ele cai na primeira tela alcançada desta lista,
 * que é onde o sino de qualquer forma o exibe.
 */
const TELAS_DE_FALLBACK = ['/chefe-de-oficina', '/andamento-veiculos', '/controle-producao'];

/**
 * transversal/at-90 — a tela para onde o clique num aviso leva: a primeira candidata que o
 * usuário alcança. É a mesma regra do número do menu, para o clique cair onde o número estava.
 */
export function telaDaNotificacao(
  notificacao: Pick<UserNotification, 'type'>,
  alcancaPath: (path: string) => boolean,
): string | null {
  const candidatas = TELAS_POR_TIPO[notificacao.type] ?? TELAS_DE_FALLBACK;
  return candidatas.find(alcancaPath) ?? null;
}

/**
 * Agrupa as notificações NÃO LIDAS por tela, respeitando o que o usuário alcança.
 *
 * `alcancaPath` é o mesmo predicado que decide o que a Sidebar renderiza — sem duplicar a regra
 * de papéis aqui dentro.
 */
export function contarNaoLidasPorTela(
  notificacoes: UserNotification[],
  alcancaPath: (path: string) => boolean,
): Record<string, number> {
  const porTela: Record<string, number> = {};

  for (const notificacao of notificacoes) {
    if (notificacao.read) continue;

    const candidatas = TELAS_POR_TIPO[notificacao.type] ?? TELAS_DE_FALLBACK;
    const tela = candidatas.find(alcancaPath);
    // Sem nenhuma tela alcançável, o aviso não tem onde ser lido — contá-lo num item que não
    // existe no menu seria um número que ninguém consegue zerar.
    if (!tela) continue;

    porTela[tela] = (porTela[tela] ?? 0) + 1;
  }

  return porTela;
}
